'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, Sparkles } from 'lucide-react';

export default function SampleDataGeneratorPage() {
  const [output, setOutput] = useState('');
  const [rowCount, setRowCount] = useState('10');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const generateData = () => {
    try {
      setError('');
      const count = parseInt(rowCount);
      if (isNaN(count) || count <= 0) {
        setError('Please enter a valid number of rows');
        return;
      }

      const headers = ['ID', 'Name', 'Email', 'Age', 'Country', 'JoinDate'];
      const names = ['John Doe', 'Jane Smith', 'Alice Johnson', 'Bob Brown', 'Charlie Davis', 'Eva Wilson'];
      const countries = ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'France'];
      
      let rows = [headers.join(',')];
      
      for (let i = 1; i <= count; i++) {
        const name = names[Math.floor(Math.random() * names.length)];
        const email = name.toLowerCase().replace(' ', '.') + i + '@example.com';
        const age = Math.floor(Math.random() * 40) + 20;
        const country = countries[Math.floor(Math.random() * countries.length)];
        const date = new Date(2023, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0];
        
        rows.push(`${i},${name},${email},${age},${country},${date}`);
      }

      setOutput(rows.join('\n'));
    } catch (err) {
      setError('Error generating sample data.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Sample Data Generator
              </h1>
            <p className="text-lg text-muted-foreground">
              Generate realistic dummy CSV data for testing and prototyping
            </p>
          </div>

          <div className="mb-6 max-w-xs mx-auto">
            <label className="text-sm font-medium mb-2 block text-center">Number of Rows to Generate</label>
            <div className="flex gap-2">
              <Input 
                type="number" 
                value={rowCount} 
                onChange={(e) => setRowCount(e.target.value)}
                className="text-center"
              />
              <Button onClick={generateData}>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate
              </Button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2 text-center">{error}</p>}
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Generated CSV Output</label>
              <Textarea
                value={output}
                readOnly
                placeholder="Your generated data will appear here..."
                className="min-h-[400px] font-mono text-sm bg-muted"
              />
            </div>
            <Button 
              onClick={copyToClipboard} 
              className="w-full"
              variant="outline"
              disabled={!output}
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </Button>
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Sample Data Generator FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. What kind of data is generated?</p><p className="text-muted-foreground">The tool generates a CSV with ID, Name, Email, Age, Country, and JoinDate fields using randomized realistic values.</p></div>
          <div><p className="font-semibold">2. Can I customize the columns?</p><p className="text-muted-foreground">Currently, the tool uses a fixed set of columns designed for general testing purposes.</p></div>
          <div><p className="font-semibold">3. Is there a limit to how many rows I can generate?</p><p className="text-muted-foreground">While there is no hard limit, generating more than 10,000 rows might slow down your browser.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
